import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { dramas, getDramaById } from "@/mocks/dramas";
import { getActorsByIds } from "@/mocks/actors";
import DramaCard from "@/components/feature/DramaCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionHeading from "@/components/feature/SectionHeading";
import EmptyState from "@/components/feature/EmptyState";
import usePageMeta from "@/hooks/usePageMeta";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const drama = id ? getDramaById(id) : undefined;
  const [copied, setCopied] = useState(false);

  usePageMeta(
    drama
      ? `${drama.title} - 剧情介绍与演员 - 善良的秘书`
      : "剧集不存在 - 善良的秘书",
    drama
      ? `《${drama.title}》由${drama.director}执导，${drama.year}年${drama.region}首播，共${drama.episodes}集。${drama.synopsis.slice(0, 60)}`
      : "该剧集不存在或已下架，返回首页浏览更多热门电视剧。"
  );

  if (!drama) {
    return (
      <div className="w-full px-4 md:px-6 py-16">
        <Breadcrumb crumbs={[{ label: "首页", to: "/" }, { label: "剧集详情" }]} />
        <div className="mt-8">
          <EmptyState
            icon="ri-emotion-sad-line"
            title="剧集不存在或已下架"
            description="你访问的剧集可能已经下架或链接有误，可以回到首页或分类页继续浏览其他热门剧集。"
            action={
              <div className="flex flex-wrap items-center justify-center gap-3">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
                >
                  <i className="ri-home-4-line"></i>
                  返回首页
                </Link>
                <Link
                  to="/category"
                  className="inline-flex items-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 whitespace-nowrap cursor-pointer hover:border-primary-300 hover:text-primary-600 transition-colors"
                >
                  <i className="ri-list-check-2"></i>
                  浏览分类
                </Link>
              </div>
            }
          />
        </div>
      </div>
    );
  }

  const cast = getActorsByIds(drama.cast);
  const related = dramas
    .filter((d) => d.id !== drama.id && d.types.some((t) => drama.types.includes(t)))
    .sort((a, b) => b.hot - a.hot)
    .slice(0, 4);

  const shareUrl = `${window.location.origin}${__BASE_PATH__}detail/${drama.id}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      // 忽略剪贴板权限异常，仍给出提示反馈
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const socials = [
    { icon: "ri-weibo-fill", label: "微博", color: "text-primary-600" },
    { icon: "ri-wechat-fill", label: "微信", color: "text-accent-600" },
    { icon: "ri-douban-fill", label: "豆瓣", color: "text-secondary-700" },
  ];

  return (
    <div className="w-full">
      <div className="relative w-full">
        <div className="absolute inset-0 h-64 md:h-80 overflow-hidden">
          <img
            src={drama.backdrop}
            alt={`${drama.title} 场景图`}
            title={`${drama.title} 剧集详情`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-secondary-950/70 to-background-50"></div>
        </div>

        <div className="relative w-full px-4 md:px-6 pt-24 md:pt-32">
          <Breadcrumb
            crumbs={[
              { label: "首页", to: "/" },
              { label: "电视剧分类", to: "/category" },
              { label: drama.title },
            ]}
          />
        </div>
      </div>

      <section className="w-full px-4 md:px-6 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 lg:gap-10">
          <div>
            <div className="relative w-44 sm:w-56 lg:w-full overflow-hidden rounded-lg border border-background-200 bg-background-100">
              <img
                src={drama.poster}
                alt={`${drama.title} 海报`}
                title={`${drama.title} 高清海报`}
                className="w-full object-cover object-top"
                style={{ aspectRatio: "2 / 3" }}
              />
            </div>
          </div>

          <div className="min-w-0">
            <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-950">
              {drama.title}
            </h1>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              {drama.types.map((t) => (
                <Link
                  key={t}
                  to={`/category?type=${encodeURIComponent(t)}`}
                  className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900 hover:bg-secondary-200 transition-colors"
                >
                  {t}
                </Link>
              ))}
              <span className="flex items-center gap-1 rounded-md bg-primary-50 px-2.5 py-1 text-xs text-primary-700">
                <i className="ri-fire-line"></i>
                热度 {drama.hot}
              </span>
            </div>

            <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <MetaItem label="导演" value={drama.director} />
              <MetaItem label="地区" value={drama.region} />
              <MetaItem label="首播时间" value={`${drama.year} 年`} />
              <MetaItem label="集数" value={`全 ${drama.episodes} 集`} />
            </dl>

            <div className="mt-5">
              <p className="text-sm text-foreground-500">主演</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {cast.map((a) => (
                  <Link
                    key={a.id}
                    to={`/actor/${a.id}`}
                    className="flex items-center gap-2 rounded-full border border-background-200 bg-background-50 py-1 pl-1 pr-3 hover:border-primary-300 transition-colors"
                  >
                    <span className="h-7 w-7 overflow-hidden rounded-full">
                      <img src={a.avatar} alt={`${a.name} 头像`} className="h-full w-full object-cover object-top" />
                    </span>
                    <span className="text-sm text-foreground-800">{a.name}</span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={copyLink}
                className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
              >
                <i className={copied ? "ri-check-line" : "ri-link"}></i>
                {copied ? "链接已复制" : "复制链接"}
              </button>
              {socials.map((s) => (
                <button
                  key={s.label}
                  type="button"
                  onClick={() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  title={`分享到${s.label}（演示）`}
                  className="flex h-10 items-center gap-2 rounded-md border border-background-200 px-4 text-sm text-foreground-700 whitespace-nowrap cursor-pointer hover:border-primary-300 hover:text-primary-600 transition-colors"
                >
                  <i className={`${s.icon} ${s.color}`}></i>
                  {s.label}
                </button>
              ))}
            </div>
            {copied && (
              <p className="mt-2 flex items-center gap-1 text-xs text-accent-700 animate-soft-fade">
                <i className="ri-information-line"></i>
                演示分享：链接已复制到剪贴板，未真实发布到社交平台。
              </p>
            )}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 pb-12">
        <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
          <SectionHeading title="剧情介绍" subtitle="分段落梳理剧集故事脉络" />
          <div className="space-y-4 text-sm md:text-base leading-relaxed text-foreground-700">
            <p>{drama.synopsis}</p>
            <p>
              在叙事上，该剧选择了多线并进的群像结构，主线与支线彼此呼应。创作者把大量篇幅留给了人物关系的铺垫，
              让每一次选择都显得有迹可循。美术与摄影同样考究，整体色调统一，氛围感强烈。
            </p>
            <p>
              如果你喜欢节奏沉稳、人物立体的作品，《{drama.title}》值得放进你的追剧清单。你也可以通过下方的相关推荐，
              找到同类型的更多剧集。
            </p>
          </div>
        </div>
      </section>

      {cast.length > 0 && (
        <section className="w-full px-4 md:px-6 pb-12">
          <SectionHeading
            title="主要演员"
            subtitle="点击演员卡片查看个人资料与参演作品"
            accent
          />
          <div className="flex gap-4 overflow-x-auto pb-2 sm:grid sm:grid-cols-3 lg:grid-cols-5 sm:overflow-visible">
            {cast.map((a) => (
              <Link
                key={a.id}
                to={`/actor/${a.id}`}
                className="group w-40 shrink-0 sm:w-auto overflow-hidden rounded-lg border border-background-200 bg-background-50 text-center transition-all hover:border-primary-300 hover:-translate-y-0.5"
              >
                <div className="w-full overflow-hidden" style={{ aspectRatio: "1 / 1" }}>
                  <img
                    src={a.avatar}
                    alt={`${a.name} 演员头像`}
                    title={`${a.name} 演员资料`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-3">
                  <p className="font-heading text-sm font-bold text-foreground-950 group-hover:text-primary-600 transition-colors">
                    {a.name}
                  </p>
                  <p className="mt-0.5 text-xs text-foreground-500">{a.role}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {related.length > 0 && (
        <section className="w-full border-t border-background-200 bg-background-100">
          <div className="w-full px-4 md:px-6 py-10 md:py-14">
            <SectionHeading
              title="相关推荐"
              subtitle={`与《${drama.title}》风格相近的剧集`}
              action={
                <Link
                  to={`/category?type=${encodeURIComponent(drama.types[0])}`}
                  className="hidden sm:flex items-center gap-1 text-sm text-foreground-600 whitespace-nowrap cursor-pointer hover:text-primary-600 transition-colors"
                >
                  更多同类
                  <i className="ri-arrow-right-s-line"></i>
                </Link>
              }
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {related.map((d) => (
                <DramaCard key={d.id} drama={d} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function MetaItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-3">
      <dt className="w-20 shrink-0 text-foreground-500">{label}</dt>
      <dd className="text-foreground-900">{value}</dd>
    </div>
  );
}